export type Currency = "SGD" | "RM";

interface CurrencyConfig {
  majorUnit: string;
  minorUnit: string;
}

const CURRENCY_CONFIG: Record<Currency, CurrencyConfig> = {
  SGD: {
    majorUnit: "Dolar Singapura",
    minorUnit: "Sen",
  },
  RM: {
    majorUnit: "Ringgit",
    minorUnit: "Sen",
  },
};

const ONES = [
  "kosong",
  "satu",
  "dua",
  "tiga",
  "empat",
  "lima",
  "enam",
  "tujuh",
  "lapan",
  "sembilan",
  "sepuluh",
  "sebelas",
  "dua belas",
  "tiga belas",
  "empat belas",
  "lima belas",
  "enam belas",
  "tujuh belas",
  "lapan belas",
  "sembilan belas",
];

const TENS = [
  "",
  "",
  "dua puluh",
  "tiga puluh",
  "empat puluh",
  "lima puluh",
  "enam puluh",
  "tujuh puluh",
  "lapan puluh",
  "sembilan puluh",
];

function numberToWords(n: number): string {
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new Error("Number must be a non-negative safe integer");
  }

  if (n < 20) {
    return ONES[n];
  }

  if (n < 100) {
    const remainder = n % 10;

    return remainder ? `${TENS[Math.floor(n / 10)]} ${ONES[remainder]}` : TENS[Math.floor(n / 10)];
  }

  if (n < 200) {
    const remainder = n - 100;

    return remainder ? `seratus ${numberToWords(remainder)}` : "seratus";
  }

  if (n < 1000) {
    const hundreds = Math.floor(n / 100);
    const remainder = n % 100;

    return remainder
      ? `${ONES[hundreds]} ratus ${numberToWords(remainder)}`
      : `${ONES[hundreds]} ratus`;
  }

  if (n < 2000) {
    const remainder = n - 1000;

    return remainder ? `seribu ${numberToWords(remainder)}` : "seribu";
  }

  if (n < 1_000_000) {
    const thousands = Math.floor(n / 1000);
    const remainder = n % 1000;

    return remainder
      ? `${numberToWords(thousands)} ribu ${numberToWords(remainder)}`
      : `${numberToWords(thousands)} ribu`;
  }

  if (n < 1_000_000_000) {
    const millions = Math.floor(n / 1_000_000);
    const remainder = n % 1_000_000;

    return remainder
      ? `${millions === 1 ? "sejuta" : `${numberToWords(millions)} juta`} ${numberToWords(remainder)}`
      : millions === 1
        ? "sejuta"
        : `${numberToWords(millions)} juta`;
  }

  if (n < 1_000_000_000_000) {
    const billions = Math.floor(n / 1_000_000_000);
    const remainder = n % 1_000_000_000;

    return remainder
      ? `${numberToWords(billions)} bilion ${numberToWords(remainder)}`
      : `${numberToWords(billions)} bilion`;
  }

  throw new Error("Amount exceeds the supported limit");
}

export function amountToWords(amount: number, currency: Currency): string {
  if (!Number.isFinite(amount) || amount < 0) {
    throw new Error("Amount must be a valid non-negative number");
  }

  if (amount >= 1_000_000_000_000) {
    throw new Error("Amount exceeds the supported limit");
  }

  const totalMinorUnits = Math.round((amount + Number.EPSILON) * 100);

  const major = Math.floor(totalMinorUnits / 100);
  const minor = totalMinorUnits % 100;

  const { majorUnit, minorUnit } = CURRENCY_CONFIG[currency];

  const parts = [majorUnit, numberToWords(major)];

  if (minor > 0) {
    parts.push("dan", numberToWords(minor), minorUnit);
  }

  return `${parts.join(" ")} Sahaja`;
}
